import { useState } from 'react';
import { X, Download, FileJson, FileSpreadsheet } from 'lucide-react';
import { api } from '@/shared/services/api';
import { Select } from '@/shared/components/Select';
import { Spinner } from '@/shared/components/Spinner';
import { cn } from '@/shared/utils/cn';
import { downloadBlob } from '@/shared/utils/download';
import { taskStatusMeta, taskStatusOrder } from '@/shared/lib/classificationMeta';
import { TaskStatus } from '@/types/task';
import toast from 'react-hot-toast';

interface ExportJiraModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ExportScope = 'all' | 'status';

interface ExportJiraRequestBody {
  projectKey: string;
  status?: TaskStatus;
}

export function ExportJiraModal({ isOpen, onClose }: ExportJiraModalProps) {
  const [projectKey, setProjectKey] = useState('PROJ');
  const [exportFormat, setExportFormat] = useState<'json' | 'csv'>('csv');
  const [exportScope, setExportScope] = useState<ExportScope>('all');
  const [status, setStatus] = useState<TaskStatus>(taskStatusOrder[0]);
  const [isExporting, setIsExporting] = useState(false);

  if (!isOpen) return null;

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const endpoint = exportFormat === 'json'
        ? '/tasks/export/jira/json'
        : '/tasks/export/jira/csv';

      const body = {
        projectKey,
        ...(exportScope === 'status' ? { status } : {}),
      } satisfies ExportJiraRequestBody;

      const response = await api.post(endpoint, body, {
        responseType: 'blob'
      });

      downloadBlob(response.data, `jira_import_${Date.now()}.${exportFormat}`);

      toast.success(`Tasks exported to Jira ${exportFormat.toUpperCase()} successfully`);
      onClose();
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export tasks');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 animate-fade-in">
      <div className="surface-overlay flex max-h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-xl shadow-modal animate-slide-up">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">Export to Jira</h2>
          <button
            onClick={onClose}
            className="btn-icon btn-ghost btn-sm"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {/* Info */}
          <div className="sev sev-info rounded-lg px-4 py-3 text-sm">
            {exportScope === 'status'
              ? `Exporting tasks with status: ${taskStatusMeta[status].label}`
              : 'Exporting all tasks'}
          </div>

          {/* Project Key */}
          <div>
            <label htmlFor="jira-project-key" className="label">
              Jira Project Key
            </label>
            <input
              id="jira-project-key"
              type="text"
              value={projectKey}
              onChange={(e) => setProjectKey(e.target.value.toUpperCase())}
              placeholder="e.g., PROJ, DEV, TASK"
              className="input mt-1.5"
              maxLength={10}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              The project key where tasks will be imported in Jira
            </p>
          </div>

          {/* Export Scope */}
          <fieldset className="m-0 border-0 p-0">
            <legend className="label">Export Scope</legend>
            <div className="mt-1.5 space-y-2">
              <label
                className={cn(
                  'flex cursor-pointer items-center rounded-md border p-3 transition-colors',
                  exportScope === 'all'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-muted'
                )}
              >
                <input
                  type="radio"
                  name="scope"
                  value="all"
                  checked={exportScope === 'all'}
                  onChange={() => setExportScope('all')}
                  className="mr-3 accent-primary"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground">All tasks</div>
                  <div className="text-xs text-muted-foreground">
                    Export every task
                  </div>
                </div>
              </label>

              <div
                className={cn(
                  'rounded-md border transition-colors',
                  exportScope === 'status'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-muted'
                )}
              >
                <label className="flex cursor-pointer items-center p-3">
                  <input
                    type="radio"
                    name="scope"
                    value="status"
                    checked={exportScope === 'status'}
                    onChange={() => setExportScope('status')}
                    className="mr-3 accent-primary"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-foreground">Only tasks with a specific status</div>
                    <div className="text-xs text-muted-foreground">
                      Limit the export to a single status
                    </div>
                  </div>
                </label>

                {exportScope === 'status' && (
                  <div className="border-t border-border px-3 pb-3 pt-3">
                    <label htmlFor="jira-export-status" className="label">
                      Status
                    </label>
                    <Select
                      id="jira-export-status"
                      className="mt-1.5"
                      value={status}
                      onChange={(v) => setStatus(v as TaskStatus)}
                      options={taskStatusOrder.map((s) => ({ value: s, label: taskStatusMeta[s].label }))}
                    />
                  </div>
                )}
              </div>
            </div>
          </fieldset>

          {/* Export Format */}
          <fieldset className="m-0 border-0 p-0">
            <legend className="label">Export Format</legend>
            <div className="mt-1.5 space-y-2">
              <label
                className={cn(
                  'flex cursor-pointer items-center rounded-md border p-3 transition-colors',
                  exportFormat === 'csv'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-muted'
                )}
              >
                <input
                  type="radio"
                  name="format"
                  value="csv"
                  checked={exportFormat === 'csv'}
                  onChange={() => setExportFormat('csv')}
                  className="mr-3 accent-primary"
                />
                <FileSpreadsheet className="mr-2 h-5 w-5 text-muted-foreground" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground">CSV Format</div>
                  <div className="text-xs text-muted-foreground">
                    Compatible with Jira&apos;s CSV importer (recommended)
                  </div>
                </div>
              </label>

              <label
                className={cn(
                  'flex cursor-pointer items-center rounded-md border p-3 transition-colors',
                  exportFormat === 'json'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-muted'
                )}
              >
                <input
                  type="radio"
                  name="format"
                  value="json"
                  checked={exportFormat === 'json'}
                  onChange={() => setExportFormat('json')}
                  className="mr-3 accent-primary"
                />
                <FileJson className="mr-2 h-5 w-5 text-muted-foreground" />
                <div className="flex-1">
                  <div className="text-sm font-medium text-foreground">JSON Format</div>
                  <div className="text-xs text-muted-foreground">
                    For Jira REST API import
                  </div>
                </div>
              </label>
            </div>
          </fieldset>

          {/* Instructions */}
          <div className="card p-4">
            <h3 className="mb-2 text-sm font-semibold text-foreground">Import Instructions</h3>
            <ul className="list-inside list-disc space-y-1 text-xs text-muted-foreground">
              <li>Go to Jira → Settings → System → External System Import</li>
              <li>Select CSV or JSON importer</li>
              <li>Upload the downloaded file</li>
              <li>Map fields and complete the import</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex shrink-0 justify-end gap-3 border-t border-border px-6 py-4">
          <button
            onClick={onClose}
            className="btn-secondary"
            disabled={isExporting}
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting || !projectKey}
            className="btn-primary"
          >
            {isExporting ? (
              <>
                <Spinner className="text-primary-foreground" />
                Exporting…
              </>
            ) : (
              <>
                <Download className="h-4 w-4" />
                Export to Jira
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
